import { startTransition, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import type { RootState } from '@redux/RootReducer';

import { Button, Checkbox, Tag, Text } from '@nvision/ui';

import { Card } from '@/components/card';
import { type ContextAction, ContextActions } from '@/components/new';
import {
  useBranches,
  useCargoTypes,
  useCarrierServices,
  useCarriers,
  useCompanyUsers,
  useLinerTypes,
  usePricingTypes,
  useTariffTypes,
} from '@/modules/entities';

import { ROUTES } from '../../../config';
import { useTariffActions } from '../../../hooks';
import type { TariffSummary } from '../../../models';
import { PricingCard } from '../pricing-card/pricing-card';

import styles from './tariff-card.module.css';

interface TariffCardProps {
  tariff: TariffSummary;
}

export const TariffCard: React.FC<TariffCardProps> = ({ tariff }) => {
  const navigate = useNavigate();
  const { changeEffectiveDates, classRateTariff, deleteTariff, exportTariff } = useTariffActions();
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  const { data: carriers } = useCarriers({ searchParams: { ids: [String(tariff.carrierKey)] } });
  const carrierName = carriers.map[String(tariff.carrierKey)]?.carrierName ?? tariff.carrierKey ?? '—';

  const { data: branches } = useBranches({ searchParams: { ids: [String(tariff.branchKey)] } });
  const branchName = branches.map[String(tariff.branchKey)]?.label ?? tariff.branchKey ?? '—';

  const { data: services } = useCarrierServices({ search: { serviceKey: String(tariff.serviceKey) } });
  const serviceDescription = services.list[0]?.description ?? tariff.serviceKey ?? '—';

  const { data: cargoTypes } = useCargoTypes();
  const cargoTypeName =
    cargoTypes.list.find((type) => type.codeId === tariff.cargoTypeCode)?.label ?? tariff.cargoTypeCode ?? '—';

  const { data: linerTypes } = useLinerTypes();
  const linerTypeName =
    linerTypes.list.find((type) => type.codeId === tariff.linerType)?.label ?? tariff.linerType ?? '—';

  const { data: pricingTypes } = usePricingTypes();
  const pricingTypeName = pricingTypes.map[tariff.pricingType]?.label ?? tariff.pricingType ?? '—';

  const { data: tariffTypes } = useTariffTypes();
  const tariffTypeName = tariffTypes.map[tariff.tariffType]?.label ?? tariff.tariffType ?? '—';

  const selectedCompany = useSelector((state: RootState) => state.auth.selectedCompany, shallowEqual);
  const { data: companyUsers } = useCompanyUsers({
    companyId: String(selectedCompany?.companyId ?? ''),
    searchParams: { ids: [tariff.createdBy, tariff.modifiedBy].filter((id) => id !== null).map(String) },
  });
  const createdByName = companyUsers.map[String(tariff.createdBy)]?.fullName ?? tariff.createdByName ?? '—';
  const modifiedByName = companyUsers.map[String(tariff.modifiedBy)]?.fullName ?? tariff.modifiedByName ?? '—';

  const actions: ContextAction[] = [
    {
      label: 'Class Rate Tariff',
      onClick: classRateTariff,
      type: 'default',
    },
    {
      label: 'Change Effective Dates',
      onClick: changeEffectiveDates,
      type: 'default',
    },
    {
      label: 'Export',
      onClick: exportTariff,
      type: 'default',
    },
    {
      label: 'Delete',
      onClick: deleteTariff,
      separator: true,
      type: 'danger',
    },
  ];

  const handleClick = () => {
    startTransition(() => {
      navigate(ROUTES.tariff(String(tariff.tariffKey)));
    });
  };

  return (
    <>
      <Card onClick={handleClick} style={{ cursor: 'pointer' }}>
        <Card.Header>
          <div className={styles.headerContainer}>
            <div className={styles.headerContent}>
              <div className={styles.keyContainer}>
                <div className={styles.keyContent}>
                  <Checkbox className={styles.checkbox} size="sm" />
                  <Text size="sm" slot="description" weight="semibold">
                    Tariff Key
                  </Text>
                  <Button
                    className={styles.tagButton}
                    style={{ backgroundColor: 'unset' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsPricingOpen(true);
                    }}
                  >
                    <Tag success>
                      <Text size="sm">{tariff.tariffKey}</Text>
                    </Tag>
                  </Button>
                </div>
              </div>
              <div className={styles.summary}>
                <Card.Field horizontal>
                  <Text size="sm" elementType="div">
                    Tariff Name:
                  </Text>
                  <Text size="sm" elementType="div" weight="semibold">
                    {tariff.tariffName ?? '—'}
                  </Text>
                </Card.Field>
                <Card.Field horizontal>
                  <Text size="sm" elementType="div">
                    Effective From:
                  </Text>
                  <Text size="sm" elementType="div" weight="semibold">
                    {tariff.effectiveDate ? format(tariff.effectiveDate, 'dd-MMM-yyyy') : '—'}
                  </Text>
                </Card.Field>
                <Card.Field horizontal>
                  <Text size="sm" elementType="div">
                    Effective To:
                  </Text>
                  <Text size="sm" elementType="div" weight="semibold">
                    {tariff.expirationDate ? format(tariff.expirationDate, 'dd-MMM-yyyy') : '—'}
                  </Text>
                </Card.Field>
              </div>
            </div>
            <ContextActions actions={actions} />
          </div>
        </Card.Header>
        <Card.Main>
          <div className={styles.cardRow}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Service Description
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {serviceDescription}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Pricing Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {pricingTypeName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Cargo Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {cargoTypeName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Created By
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {createdByName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Modified By
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {modifiedByName}
              </Text>
            </Card.Field>
            <div />
          </div>
          <div className={styles.cardRow}>
            <Card.Field>
              <Text size="xs" elementType="div">
                Transportation Provider
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {carrierName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Branch Name
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {branchName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Tariff Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {tariffTypeName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Liner Type
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {linerTypeName}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Created On
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {tariff.createdOn ? format(tariff.createdOn, 'dd-MMM-yyyy') : '—'}
              </Text>
            </Card.Field>
            <Card.Field>
              <Text size="xs" elementType="div">
                Modified On
              </Text>
              <Text size="xs" elementType="div" weight="semibold">
                {tariff.modifiedOn ? format(tariff.modifiedOn, 'dd-MMM-yyyy') : '—'}
              </Text>
            </Card.Field>
          </div>
        </Card.Main>
      </Card>
      <PricingCard isOpen={isPricingOpen} setIsOpen={setIsPricingOpen} tariffKey={tariff.tariffKey} />
    </>
  );
};
